import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Shield,
  Lock,
  EyeOff,
  Bot,
  UserCheck,
  CreditCard,
  Database,
  Ban,
  ExternalLink,
  CheckCircle2,
  XCircle,
  DollarSign,
  ArrowRight,
} from 'lucide-react';
import PrivateVideoEmbed from '../components/feed/PrivateVideoEmbed';

/**
 * /trust — Plain-English explanation of AADC's data and identity stance.
 * Distinct from /privacy-policy (legal text) and /privacy (dynamic policy).
 * Goal: a member or visitor can read this in 90 seconds and walk away
 * understanding (a) we don't sell data, (b) why we use Stripe Identity,
 * (c) what we do and don't store.
 */
export default function TrustPage() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white">
      <SEO
        title="Trust, Data & Identity"
        description="How AADC handles your data: we don't sell it, we don't train AI on it, and we use Stripe Identity only as a 'are you a real person?' check. Plain English, no legalese."
        url="/trust"
      />

      {/* Hero */}
      <div className="bg-gradient-to-r from-amber-700 to-amber-800 text-white py-14">
        <div className="container mx-auto px-4 text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-white/15 flex items-center justify-center">
            <Shield className="w-8 h-8" />
          </div>
          <h1 className="text-4xl md:text-5xl font-heading font-bold mb-3">
            Your data is yours.
          </h1>
          <p className="text-lg text-amber-100 max-w-2xl mx-auto">
            Plain English: we don&rsquo;t collect more than we need, we
            don&rsquo;t sell it, and we don&rsquo;t train AI on it.
          </p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12 max-w-3xl">
        {/* The TL;DR */}
        <Card className="mb-8 border-amber-200" data-testid="trust-tldr">
          <CardHeader className="bg-amber-50 border-b border-amber-100">
            <CardTitle className="font-heading">The short version</CardTitle>
          </CardHeader>
          <CardContent className="pt-6 space-y-3">
            <Row icon={<XCircle className="text-rose-500" />} text="We don't sell your data. Not to advertisers, not to data brokers, not to anyone." />
            <Row icon={<XCircle className="text-rose-500" />} text="We don't train AI models on your posts, photos, comments, or pet profiles." />
            <Row icon={<XCircle className="text-rose-500" />} text="We don't share your email or membership info with other clubs unless you choose to publish it." />
            <Row icon={<XCircle className="text-rose-500" />} text="We don't see what you give Stripe — we only get a verified yes/no back." />
            <Row icon={<CheckCircle2 className="text-emerald-600" />} text="We do use Stripe to verify that paid members are real, identifiable people — but Stripe holds the ID, not us." />
            <Row icon={<CheckCircle2 className="text-emerald-600" />} text="We pay Stripe ~$1.50 per verification — that cost is why verification is a paid step, not a free one." />
            <Row icon={<CheckCircle2 className="text-emerald-600" />} text="You can delete your account at any time and your data is removed." />
          </CardContent>
        </Card>

        {/* The bot/troll problem */}
        <Section icon={<Bot />} title="Why we care who's a real person">
          <p>
            A community is only as good as the people in it. Bots, AI
            agents pretending to be users, troll accounts, and people
            running multiple fake identities all corrode the trust that
            makes a club worth joining.
          </p>
          <p>
            Free members can join AADC anonymously &mdash; you only need
            an email. But for paid members (who get governance votes,
            club-creation rights, marketplace access, and more) we want
            a stronger signal that you&rsquo;re a real, unique person.
            That&rsquo;s where Stripe comes in.
          </p>
        </Section>

        {/* Stripe Identity, plain English */}
        <Section icon={<UserCheck />} title="How we use Stripe Identity">
          <p>
            When a paid member opts into identity verification, Stripe
            (a regulated payment company) checks that:
          </p>
          <ul className="list-disc pl-5 space-y-1 text-muted-foreground">
            <li>You&rsquo;re a real, unique person (not an AI or duplicate account)</li>
            <li>Your name and date of birth match a government-issued ID</li>
            <li>The selfie photo matches the photo on the ID</li>
          </ul>
          <p>
            Stripe then sends us back a single answer: <strong>verified</strong> or
            <strong> not verified</strong>. That&rsquo;s it. We don&rsquo;t see
            your ID, your selfie, your address, your ID number, or any
            biometric data &mdash; ever.
          </p>
        </Section>

        {/* Why we charge for verification */}
        <Section icon={<DollarSign />} title="Why verification costs money">
          <p>
            Stripe charges <strong>us</strong> a fee for every identity
            verification (about $1.50 per check). That cost is passed
            through to the verification step &mdash; we&rsquo;re not
            making money on verification, we&rsquo;re paying Stripe.
          </p>
          <p>
            What you get in return: more of the Club&rsquo;s activities
            and website features unlock for verified members.
            Governance voting, club creation, marketplace, member
            directories opt-in, premium content, partner-program
            participation. The verification fee is the price of trust
            in a community that aims to keep bots, AI agents, and
            duplicate accounts out.
          </p>
        </Section>

        {/* One-way data flow */}
        <Section icon={<ArrowRight />} title="The data flow is one-way">
          <p>
            When you go through Stripe&rsquo;s verification form, you
            give Stripe a lot of information &mdash; ID document,
            selfie, sometimes an address. <strong>We don&rsquo;t see
            any of that.</strong> We don&rsquo;t see the form, we
            don&rsquo;t see what you typed, we don&rsquo;t see what you
            uploaded.
          </p>
          <p>
            What Stripe sends back to AADC: a verification result
            (verified / not verified), and the name and date of birth
            that match your ID. That&rsquo;s the entire data exchange.
          </p>
          <p>
            <strong>We don&rsquo;t hand any of your data to
            advertisers, data brokers, or analytics resellers.</strong>{' '}
            We may, at some point in the future, publish{' '}
            <em>aggregate, anonymous group statistics</em> &mdash;
            for example, &ldquo;AADC has X members in Y states with Z
            average breeds per household.&rdquo; That kind of summary
            data is fine; what we will never do is pass along anything
            personal or identifying.
          </p>
        </Section>

        {/* External links and embeds */}
        <Section icon={<ExternalLink />} title="When you leave AADC, we lose control">
          <p>
            If you click a link to another website (a partner
            organization&rsquo;s site, a news article, a YouTube video,
            a rescue&rsquo;s donation page), the site you land on can
            collect anything its operator chooses to collect &mdash;
            cookies, IP address, location, browsing history. We have
            no influence over that.
          </p>
          <p>
            <strong>Embedded videos and social posts are also
            third-party beacons.</strong> When you play an embedded
            YouTube, TikTok, or Vimeo clip on an AADC page, the
            embedded service can set cookies and log your viewing
            session &mdash; even if you never leave AADC.
          </p>
          <p>
            We&rsquo;ve adopted a <strong>&ldquo;click-to-load&rdquo;</strong>
            approach for video embeds (the same idea behind
            DuckDuckGo&rsquo;s Duck Player). Instead of loading
            YouTube&rsquo;s iframe immediately, we show a thumbnail
            placeholder. <em>No request is sent to YouTube</em> until
            you click play. When you do, we load the iframe at the
            privacy-enhanced <code>youtube-nocookie.com</code> domain,
            with <code>no-referrer</code> set so YouTube can&rsquo;t
            see which AADC page you came from, and a sandboxed iframe
            so the video can&rsquo;t do anything beyond playing the
            video.
          </p>
          <p className="text-sm text-muted-foreground">
            Once you click play, YouTube does still receive your IP,
            user-agent, and viewing session &mdash; we cannot prevent
            that without proxying every video stream through our own
            servers, which would be costly and break frequently.
            What we <em>can</em> do is make sure the choice to load
            the embed is yours, not automatic, and we have.
          </p>

          {/* Live demo of the private embed */}
          <div className="mt-4 rounded-lg border bg-white p-4" data-testid="trust-private-embed-demo">
            <p className="text-xs uppercase tracking-wide text-muted-foreground mb-2 font-semibold">
              Live example &mdash; an AADC video embed:
            </p>
            <PrivateVideoEmbed
              url="https://www.youtube.com/watch?v=PCwL3-hkKrg"
              title="Demo video — AADC private embed"
            />
            <p className="text-xs text-muted-foreground mt-2">
              Inspect this page in your browser&rsquo;s network tab:
              you&rsquo;ll see <strong>no requests to youtube.com</strong>{' '}
              until you click the play button above.
            </p>
          </div>
        </Section>

        {/* Two-column: what Stripe stores vs what AADC stores */}
        <Card className="mb-8" data-testid="trust-data-table">
          <CardHeader>
            <CardTitle className="font-heading">What gets stored where</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="rounded-lg border bg-muted/30 p-4">
                <div className="flex items-center gap-2 mb-2 font-semibold">
                  <CreditCard className="w-4 h-4 text-violet-600" /> Stripe holds
                </div>
                <ul className="text-sm space-y-1 text-muted-foreground">
                  <li>&middot; Your government-issued ID image</li>
                  <li>&middot; Your selfie / biometric data</li>
                  <li>&middot; Your card &amp; billing details</li>
                  <li>&middot; Verification audit logs</li>
                </ul>
                <p className="text-xs mt-2 italic text-muted-foreground/80">
                  Held under PCI&nbsp;DSS Level&nbsp;1, EU-US Data Privacy
                  Framework, and Stripe&rsquo;s privacy policy. Biometric
                  data is deleted within 1 year.
                </p>
              </div>
              <div className="rounded-lg border bg-amber-50 p-4">
                <div className="flex items-center gap-2 mb-2 font-semibold">
                  <Database className="w-4 h-4 text-amber-700" /> AADC holds
                </div>
                <ul className="text-sm space-y-1 text-muted-foreground">
                  <li>&middot; Your email address</li>
                  <li>&middot; Your username and display name</li>
                  <li>&middot; A &ldquo;verified: yes/no&rdquo; flag from Stripe</li>
                  <li>&middot; Your verified first name and date of birth (only if you completed verification)</li>
                  <li>&middot; The content <em>you</em> chose to publish (posts, photos, pets)</li>
                </ul>
                <p className="text-xs mt-2 italic text-muted-foreground/80">
                  No biometric data, no ID document, no card numbers,
                  no CVV. Ever.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* What we don't do */}
        <Card className="mb-8 border-rose-200" data-testid="trust-never-list">
          <CardHeader className="bg-rose-50/40 border-b border-rose-100">
            <CardTitle className="font-heading flex items-center gap-2">
              <Ban className="w-5 h-5 text-rose-600" /> What AADC will never do
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <ul className="space-y-2.5">
              <NeverItem text="Sell, rent, or trade your personal data to anyone." />
              <NeverItem text="Train AI / large-language models on your private content." />
              <NeverItem text="Share your email or contact info with other members or clubs without your explicit action." />
              <NeverItem text="Track you across the rest of the web with third-party advertising pixels." />
              <NeverItem text="Use dark patterns to keep you from deleting your account." />
              <NeverItem text="Lock your data in. You can export it and walk away whenever you like." />
            </ul>
          </CardContent>
        </Card>

        {/* Money line */}
        <Card className="mb-8 bg-amber-50/50 border-amber-200" data-testid="trust-money">
          <CardContent className="pt-6">
            <h3 className="font-heading font-semibold mb-2 flex items-center gap-2">
              <Lock className="w-5 h-5 text-amber-700" /> So how does AADC make money?
            </h3>
            <p className="text-sm leading-relaxed">
              Membership dues. That&rsquo;s it. Free members are free
              forever. Paid members fund the platform, and a portion of
              every paid membership goes to dog welfare organizations
              chosen by the membership.{' '}
              <Link to="/about" className="text-amber-800 underline hover:no-underline">
                See &ldquo;Where Your Dues Go&rdquo;
              </Link>{' '}
              for the breakdown.
            </p>
          </CardContent>
        </Card>

        {/* Footer links */}
        <div className="text-center text-sm text-muted-foreground space-y-2 pt-4 border-t">
          <p>
            For the legal version with all the formal details:{' '}
            <Link to="/privacy-policy" className="text-amber-700 underline hover:no-underline">
              Privacy Policy
            </Link>
            {' '}&middot;{' '}
            <Link to="/disclaimer" className="text-amber-700 underline hover:no-underline">
              Disclaimer
            </Link>
          </p>
          <p>
            Stripe&rsquo;s own privacy policy:{' '}
            <a
              href="https://stripe.com/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-amber-700 underline hover:no-underline inline-flex items-center gap-1"
            >
              stripe.com/privacy <ExternalLink className="w-3 h-3" />
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}

function Row({ icon, text }) {
  return (
    <div className="flex items-start gap-3">
      <div className="w-5 h-5 mt-0.5 shrink-0">{icon}</div>
      <p className="text-sm leading-relaxed">{text}</p>
    </div>
  );
}

function Section({ icon, title, children }) {
  return (
    <div className="mb-8" data-testid={`trust-section-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
      <h2 className="text-2xl font-heading font-bold mb-3 flex items-center gap-3">
        <span className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center text-amber-700">
          {React.cloneElement(icon, { className: 'w-5 h-5' })}
        </span>
        {title}
      </h2>
      <div className="prose prose-amber max-w-none space-y-3 leading-relaxed text-muted-foreground">
        {children}
      </div>
    </div>
  );
}

function NeverItem({ text }) {
  return (
    <li className="flex items-start gap-3">
      <EyeOff className="w-4 h-4 text-rose-500 mt-1 shrink-0" />
      <span className="text-sm">{text}</span>
    </li>
  );
}
